/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  BuilderElement,
  ElementType,
  BuilderStage,
  Industry,
  SiteSettings,
  PageSiteSettings,
  GlobalConfig,
  BuilderState,
  BuilderAction,
  BuilderContextType,
  Template,
  TemplateExtraPage,
} from '@/types/builder';
import WebFont from 'webfontloader';
import { toast } from 'sonner';
import { ROUTES } from '@/routes/routeConfig';
import {
  createNewElement,
  generateId,
  defaultTemplate,
  getTemplateByIndustry,
  findElementById,
  updateElement,
  addElementToContainer,
  TEMPLATE_EXTRA_PAGES,
  getPopupTemplatesByIndustry,
  compileElements,
  moveElement,
  reorderElements,
  validateAndFixIds,
  duplicateElement,
  applyLogoToTemplate,
  normalizeLogoConfig,
  updateGlobalSettingsAcrossPages,
  updateGeneralSettingsAcrossPages,
  collectCustomFonts,
} from '@/utils/builder';
import {
  cleanUpOldStorageEntires,
  loadState,
  saveState,
} from '@/utils/storage';
import { useParams } from 'react-router-dom';
import {
  useModules,
  useModule,
  useCreateModule,
  useUpdateModule,
} from '@/hooks/apis/useModule';
import {
  useCreateElement,
  useUpdateElement,
  useElements,
} from '@/hooks/apis/useElement';
import { useUpdatePageElements } from '@/hooks/apis/usePageElements';
import { builderElementToImportPayload } from '@/utils/builderElementToImportPayload';
import {
  mapBuilderConfigToModule,
  mapModuleToBuilderConfig,
} from '@/utils/moduleMapper';
import { extractOid } from '@/utils/normalizeOid';
import {
  saveModuleToStorage,
  getModuleFromStorage,
  getModuleByProjectId,
} from '@/utils/moduleStorage';
import type { WebsiteModule } from '@/interfaces/module.interface';
import type { WebsiteElement } from '@/interfaces/element.interface';
import { authenticateUserPubliishAPI } from '@/utils/fileUpload';
import {
  AUTH_LOCAL_STORAGE_KEYS,
  BUILDER_INTERFACE_LOCAL_STORAGE_KEYS,
  PROJECT_LOCAL_STORAGE_KEYS,
} from '@/constants/localStorage';

// Default fallback values for safety
const DEFAULT_INDUSTRY: Industry = 'blankTemplate';
export const DEFAULT_GLOBAL_CONFIG: GlobalConfig = {
  typography: [
    {
      name: 'Default',
      fontFamily: 'Inter, sans-serif',
      fontSize: '16px',
      fontWeight: '400',
      fontStyle: 'normal',
    },
  ],
  colors: {
    background: '#FFFFFF',
    forground: '#000000',
    primary: '#7C3AED',
    secondary: '#6366F1',
    tertiary: '#F59E42',
    quaternary: '#10B981',
    link: '#2563EB',
    activeLink: '#1D4ED8',
  },
  breakpoints: {
    mobileMax: 640,
    tabletMin: 641,
    tabletMax: 1024,
    desktopMin: 1025,
  },
};

const defaultSiteSettings: SiteSettings = {
  title: 'Default',
  description: 'Default',
  language: 'EN',
  page: 'home',
  showPageOnSearchEngine: true,
  customCode: {
    endOfBodyTag: '',
    startOfBodyTag: '',
    startOfHeadTag: '',
    endOfHeadTag: '',
  },
  appleTouchIcon: undefined,
  favicon: undefined,
  googleAnalyticsMeasurementId: '',
  socialPreview: undefined,
  baseDomain: {
    url: 'example.domain.com',
  },
  customDomain: {
    type: 'free',
    freeSubDomain: 'default',
  },
  projectMetadata: {
    projectName: '',
    projectCode: '',
    stakeholders: '',
    owners: '',
    timeline: {
      designPhase: '',
      developmentPhase: '',
      qaPhase: '',
      goLivePhase: '',
    },
    deploymentTarget: '',
    cmsType: 'html',
    environmentUrls: {
      staging: '',
      development: '',
      production: '',
    },
  },
  copyLegal: {
    finalCopyDocs: '',
    ctaVerbiage: '',
    legalApproval: false,
    emailContact: '',
    phoneContact: '',
  },
  performanceAccessibility: {
    imageOptimizationRules: {
      maxFileSize: 10240,
      maxDimensions: {
        width: 1024,
        height: 1024,
      },
      formats: ['jpg', 'png', 'gif', 'svg', 'webp'],
    },
    lazyLoadSettings: {
      enabled: false,
      threshold: 0.5,
    },
    accessibilityTags: {
      altTextRequired: false,
      tabIndexEnabled: false,
      colorContrastCheck: false,
    },
    fontLoadingStrategy: 'preload',
    pageSpeedTargets: {
      mobileScore: 80,
      desktopScore: 80,
    },
  },
  seo: {
    metaTitle: '',
    metaDescription: '',
    canonicalTags: false,
    openGraphTags: {
      title: '',
      description: '',
      image: '',
      type: '',
    },
    altTextForSEO: false,
  },
  analyticsTracking: {
    gtmContainerId: '',
    ga4Setup: {
      measurementId: '',
      enabled: false,
    },
    conversionEvents: {
      scrollPercentage: 0,
      ctaClicks: false,
      purchases: false,
      formSubmissions: false,
    },
    pixelIntegrations: {
      meta: '',
      tiktok: '',
      linkedin: '',
      googleAds: '',
    },
    consentGdpr: {
      cookieBanner: false,
      trackingOptIn: false,
      privacyPolicyUrl: '',
    },
  },
  techStack: {
    frontendFramework: 'react',
    versionControl: 'github',
    hostingDeployment: 'netlify',
    backendDependencies: [],
  },
  qaSuccess: {
    deviceTesting: {
      iphone: false,
      android: false,
      tablets: false,
      desktop: false,
      browsers: [],
    },
    coreWebVitals: {
      lcpTarget: 0,
      fidTarget: 0,
      clsTarget: 0,
    },
    formSubmissionTest: {
      contactForm: false,
      upsellForm: false,
      newsletterForm: false,
    },
    fallbackChecks: {
      jsFailureHandling: false,
      missingParamHandling: false,
    },
    accessibilityScore: 0,
  },
  securityPrivacy: {
    sslCertificate: false,
    robotsTxtRules: '',
    noindexMetaTags: false,
    formSpamPrevention: {
      recaptcha: false,
      honeypot: false,
      cloudflare: false,
    },
  },
};

const defaultPageSiteSettings: PageSiteSettings = {
  title: 'Default',
  description: 'Default',
  language: 'EN',
  showPageOnSearchEngine: true,
  videoPlaybackTriggerSeconds: 10,
  playbackSeekEventTriggered: false,
  customCode: {
    endOfBodyTag: '',
    startOfBodyTag: '',
    startOfHeadTag: '',
    endOfHeadTag: '',
  },
};

const initialState: BuilderState = {
  template: defaultTemplate,
  selectedElementId: null,
  viewMode: 'desktop',
  isDragging: false,
  stage: 'editor',
  userConfig: null,
  history: {
    past: [],
    future: [],
  },
  zoom: 1,
  extraPages: [],
  popupTemplates: [],
  selectedPage: 'home',
  selectedInterfaceId: '',
  siteSettings: defaultPageSiteSettings,
  generalSettings: defaultSiteSettings,
  carouselActiveIndex: {},
  components: undefined,
};

// Create the reducer
const builderReducer = (
  state: BuilderState,
  action: BuilderAction
): BuilderState => {
  switch (action.type) {
    case 'SET_TEMPLATE':
      return {
        ...state,
        template: action.payload,
        history: {
          past: [...state.history.past, { template: state.template, popupTemplates: state.popupTemplates }],
          future: [],
        },
      };
    case 'SET_SELECTED_INTERFACE_ID':
      return {
        ...state,
        selectedInterfaceId: action.payload,
      };
    case 'ADD_EXTRA_PAGE': {
      saveState(`${state?.userConfig?.businessName}-${action.payload.title}`, {
        ...state,
        template: getTemplateByIndustry(
          'blankTemplate',
          state.userConfig?.businessName || '',
          state.userConfig?.globalConfig || DEFAULT_GLOBAL_CONFIG
        ),
        stage: 'editor',
        extraPages: [...state.extraPages, action.payload],
        selectedPage: action.payload.title,
        dynamicValues: action.payload.dynamicValues,
        generalSettings: undefined,
        siteSettings: {
          ...state.siteSettings,
        },
      });

      state.extraPages?.map((page) => {
        const pageState = loadState(
          `${state?.userConfig?.businessName}-${page.title}`
        );
        if (pageState) {
          saveState(`${state?.userConfig?.businessName}-${page.title}`, {
            ...pageState,
            extraPages: [...(pageState.extraPages || []), action.payload],
          });
        }
      });

      const homePageState = loadState(`${state?.userConfig?.businessName}`);
      if (homePageState) {
        saveState(`${state?.userConfig?.businessName}`, {
          ...homePageState,
          extraPages: [...(state.extraPages || []), action.payload],
        });
      }

      return {
        ...state,
        extraPages: [...state.extraPages, action.payload],
      };
    }
    case 'SET_INTERFACES': {
      const interfacesAsPages = action.payload.map((iface) => ({
        id: iface.id,
        title: iface.title || iface.page_type || 'Untitled',
        industryName: iface.page_type || 'default',
      }) as TemplateExtraPage);
      return {
        ...state,
        extraPages: interfacesAsPages,
      };
    }
    case 'SET_CAROUSEL_ACTIVE_INDEX': {
      const { elementId, index } = action.payload;
      return {
        ...state,
        carouselActiveIndex: {
          ...(state.carouselActiveIndex || {}),
          [elementId]: index,
        },
      };
    }
    case 'SELECT_ELEMENT':
      return {
        ...state,
        selectedElementId: action.payload,
      };

    case 'UPDATE_ELEMENT': {
      // Mark updated element as needing sync (but not new)
      const updatedElementWithFlags = {
        ...action.payload.element,
        _needsSync: true,
        _isNew: false, // Clear isNew flag if it was set
      };
      const updatedElements = updateElement(
        state.template.elements,
        action.payload.id,
        updatedElementWithFlags
      );

      const updatedTemplate = {
        ...state.template,
        elements: updatedElements,
      };

      const updatedPopupElements = updateElement(
        state.popupTemplates.find((popup) => popup.id === state.selectedPopupID)
          ?.elements || [],
        action.payload.id,
        action.payload.element
      );

      const updatedPopupTemplate = {
        ...state.popupTemplates.find(
          (popup) => popup.id === state.selectedPopupID
        ),
        elements: updatedPopupElements,
      };

      const stateNew = {
        ...state,
        template: updatedTemplate,
        popupTemplates: [
          ...state.popupTemplates.map((popup) =>
            popup.id === state.selectedPopupID ? updatedPopupTemplate : popup
          ),
        ],
        history: {
          past: [...state.history.past, { template: state.template, popupTemplates: state.popupTemplates }],
          future: [],
        },
      };

      const landingPageID = state.userConfig?.businessName;
      const pageTemplateId =
        state.selectedPage === 'home'
          ? landingPageID
          : `${landingPageID}-${state.selectedPage}`;

      saveState(pageTemplateId, stateNew);
      return stateNew;
    }

    case 'MARK_ELEMENT_AS_SYNCED': {
      const { elementId } = action.payload;

      const clearFlags = (elements: BuilderElement[]): BuilderElement[] => {
        return elements.map((el) => {
          if (el.id === elementId) {
            const { _needsSync, _isNew, ...rest } = el;
            return rest;
          }
          if (el.children) {
            return { ...el, children: clearFlags(el.children) };
          }
          return el;
        });
      };

      const updatedElements = clearFlags(state.template.elements);

      return {
        ...state,
        template: {
          ...state.template,
          elements: updatedElements,
        },
      };
    }

    case 'UPDATE_TEMPLATE_ROOT_CONTAINER': {
      const updatedTemplate = {
        ...state.template,
        rootContainer: {
          ...state.template.rootContainer,
          styles: action.payload.styles
            ? {
                ...state.template.rootContainer?.styles,
                ...action.payload.styles,
              }
            : state.template.rootContainer?.styles,
          responsiveStyles: action.payload.responsiveStyles
            ? {
                ...state.template.rootContainer?.responsiveStyles,
                ...action.payload.responsiveStyles,
              }
            : state.template.rootContainer?.responsiveStyles,
          hoverStyles: action.payload.hoverStyles
            ? {
                ...state.template.rootContainer?.hoverStyles,
                ...action.payload.hoverStyles,
              }
            : state.template.rootContainer?.hoverStyles,
          responsiveHoverStyles: action.payload.responsiveHoverStyles
            ? {
                ...state.template.rootContainer?.responsiveHoverStyles,
                ...action.payload.responsiveHoverStyles,
              }
            : state.template.rootContainer?.responsiveHoverStyles,
        },
      };

      const stateNew = {
        ...state,
        template: updatedTemplate,
        history: {
          past: [...state.history.past, { template: state.template, popupTemplates: state.popupTemplates }],
          future: [],
        },
      };

      const landingPageID = state.userConfig?.businessName;
      const pageTemplateId =
        state.selectedPage === 'home'
          ? landingPageID
          : `${landingPageID}-${state.selectedPage}`;

      saveState(pageTemplateId, stateNew);
      return stateNew;
    }

    case 'ADD_ELEMENT': {
      const newElement = createNewElement(action.payload.type);
      // Mark as new and needing sync
      newElement._isNew = true;
      newElement._needsSync = true;

      // If iconContent is provided and element is an icon, set the content
      if (action.payload.iconContent && action.payload.type === 'icon') {
        newElement.content = action.payload.iconContent;
      }

      if (action.payload.parentTabId) {
        newElement.parentTabId = action.payload.parentTabId;
      }

      // For logo elements, apply global logo if it exists
      if (action.payload.type === 'logo' && state.userConfig?.logo) {
        const logo = state.userConfig.logo;
        const businessName = state.userConfig?.businessName || 'Logo';
        const logoConfig = normalizeLogoConfig(logo);

        if (logoConfig?.desktop) {
          // Replace default placeholder with actual logo
          newElement.children = [
            {
              id: generateId(),
              type: 'image' as ElementType,
              src: logoConfig.desktop,
              srcResponsive: {
                desktop: logoConfig.desktop,
                tablet: logoConfig.tablet || logoConfig.desktop,
                mobile: logoConfig.mobile || logoConfig.desktop,
              },
              alt: businessName,
              styles: {
                height: 'auto',
                maxHeight: '40px',
                width: 'auto',
                objectFit: 'contain',
              },
            },
          ];
        }
      }

      let updatedPopupTemplates = state.popupTemplates;
      if (state.selectedPopupID && state.selectedPopupID !== 'none') {
        const selectedPopup = state.popupTemplates.find(
          (popup) => popup.id === state.selectedPopupID
        );

        if (selectedPopup) {
          const updatedPopupElements = addElementToContainer(
            selectedPopup.elements,
            action.payload.containerId,
            newElement
          );
          const updatedPopupTemplate = {
            ...selectedPopup,
            elements: updatedPopupElements,
          };

          updatedPopupTemplates = state.popupTemplates.map((popup) =>
            popup.id === state.selectedPopupID ? updatedPopupTemplate : popup
          );
        }
      }

      const updatedElements = addElementToContainer(
        state.template.elements,
        action.payload.containerId,
        newElement
      );
      const updatedTemplate = {
        ...state.template,
        elements: updatedElements,
      };

      return {
        ...state,
        template: updatedTemplate,
        popupTemplates: updatedPopupTemplates,
        selectedElementId: newElement.id,
        history: {
          past: [...state.history.past, { template: state.template, popupTemplates: state.popupTemplates }],
          future: [],
        },
      };
    }

    case 'ADD_ROOT_ELEMENT': {
      // Mark as new and needing sync
      const elementWithFlags = {
        ...action.payload,
        _isNew: true,
        _needsSync: true,
      };
      const updatedTemplate = {
        ...state.template,
        elements: [...state.template.elements, elementWithFlags],
      };

      // Also handle popup template if we're editing one
      let updatedPopupTemplates = state.popupTemplates;
      if (state.selectedPopupID && state.selectedPopupID !== 'none') {
        const selectedPopup = state.popupTemplates.find(
          (popup) => popup.id === state.selectedPopupID
        );

        if (selectedPopup) {
          const updatedPopupTemplate = {
            ...selectedPopup,
            elements: [...selectedPopup.elements, action.payload],
          };

          updatedPopupTemplates = state.popupTemplates.map((popup) =>
            popup.id === state.selectedPopupID ? updatedPopupTemplate : popup
          );
        }
      }

      return {
        ...state,
        template: updatedTemplate,
        popupTemplates: updatedPopupTemplates,
        selectedElementId: action.payload.id,
        history: {
          past: [...state.history.past, { template: state.template, popupTemplates: state.popupTemplates }],
          future: [],
        },
      };
    }

    case 'INSERT_ELEMENT_TREE': {
      const { element, containerId, targetIndex, insertInto } = action.payload;

      if (
        insertInto === 'popup' &&
        state.selectedPopupID &&
        state.selectedPopupID !== 'none'
      ) {
        const selectedPopup = state.popupTemplates.find(
          (popup) => popup.id === state.selectedPopupID
        );

        if (!selectedPopup) {
          return state;
        }

        const updatedPopupElements = containerId
          ? addElementToContainer(
              selectedPopup.elements,
              containerId,
              element,
              targetIndex
            )
          : [...selectedPopup.elements, element];

        const updatedPopupTemplate = {
          ...selectedPopup,
          elements: updatedPopupElements,
        };

        return {
          ...state,
          popupTemplates: state.popupTemplates.map((popup) =>
            popup.id === state.selectedPopupID ? updatedPopupTemplate : popup
          ),
          selectedElementId: element.id,
          history: {
            past: [
              ...state.history.past,
              { template: state.template, popupTemplates: state.popupTemplates },
            ],
            future: [],
          },
        };
      }

      const updatedElements = containerId
        ? addElementToContainer(
            state.template.elements,
            containerId,
            element,
            targetIndex
          )
        : [...state.template.elements, element];

      return {
        ...state,
        template: {
          ...state.template,
          elements: updatedElements,
        },
        selectedElementId: element.id,
        history: {
          past: [
            ...state.history.past,
            { template: state.template, popupTemplates: state.popupTemplates },
          ],
          future: [],
        },
      };
    }

    case 'UPDATE_TAB_CONFIG': {
      const updatedTemplate = {
        ...state.template,
        elements: state.template.elements.map((element) => {
          if (element.id === action.payload.id) {
            return {
              ...element,
              tabConfig: action.payload.tabConfig,
            };
          }
          return element;
        }),
      };

      return {
        ...state,
        template: updatedTemplate,
        history: {
          past: [...state.history.past, { template: state.template, popupTemplates: state.popupTemplates }],
          future: [],
        },
      };
    }

    case 'DUPLICATE_ELEMENT': {
      // Find the original element
      const originalElement = findElementById(
        state.template.elements,
        action.payload.elementId
      );
      if (!originalElement) return state;

      // Create a duplicate with new IDs
      const duplicatedElement = duplicateElement(originalElement);

      // Add the duplicated element to the target container at the specified index
      const updatedElements = addElementToContainer(
        state.template.elements,
        action.payload.targetContainerId,
        duplicatedElement,
        action.payload.targetIndex
      );

      const updatedTemplate = {
        ...state.template,
        elements: updatedElements,
      };

      return {
        ...state,
        template: updatedTemplate,
        selectedElementId: duplicatedElement.id,
        history: {
          past: [...state.history.past, { template: state.template, popupTemplates: state.popupTemplates }],
          future: [],
        },
      };
    }

    case 'SET_VIEW_MODE':
      return {
        ...state,
        viewMode: action.payload,
      };

    case 'SET_DRAGGING':
      return {
        ...state,
        isDragging: action.payload,
      };

    case 'SAVE_TEMPLATE':
      // Save to localStorage (already handled by saveState calls in other actions)
      // API sync will be handled in the provider's useEffect
      return state;

    case 'SET_STAGE':
      // When going back to registration, clear module creation attempt flag
      // to allow module creation/update when form is submitted again
      if (action.payload === 'registration') {
        // Clear any stale create attempt flags
        const keys = Object.keys(localStorage);
        keys.forEach((key) => {
          if (key.startsWith('module_create_attempt_')) {
            localStorage.removeItem(key);
          }
        });
      }
      return {
        ...state,
        stage: action.payload,
      };

    case 'SET_USER_CONFIG': {
      const updatedState = {
        ...state,
        userConfig: action.payload,
      };

      // Check if global settings changed (logo, colors, typography, breakpoints, business name)
      const oldUserConfig = state.userConfig;
      const newUserConfig = action.payload;

      const logoChanged =
        JSON.stringify(oldUserConfig?.logo) !==
        JSON.stringify(newUserConfig.logo);
      const globalConfigChanged =
        JSON.stringify(oldUserConfig?.globalConfig) !==
        JSON.stringify(newUserConfig.globalConfig);
      const businessNameChanged =
        oldUserConfig?.businessName !== newUserConfig.businessName;

      const globalSettingsChanged =
        logoChanged || globalConfigChanged || businessNameChanged;

      console.log('🔄 SET_USER_CONFIG - Global settings change detected:', {
        logoChanged,
        globalConfigChanged,
        businessNameChanged,
        overall: globalSettingsChanged,
      });

      if (globalSettingsChanged && action.landingPageID) {
        // Update global settings across all pages in localStorage
        updateGlobalSettingsAcrossPages(
          action.landingPageID,
          newUserConfig,
          state.extraPages || []
        );

        // Apply changes to current page state (for immediate UI update)
        const updatedTemplate = {
          ...state.template,
          elements: applyLogoToTemplate(
            state.template.elements,
            newUserConfig.logo,
            newUserConfig.businessName
          ),
        };

        const updatedPopupTemplates = state.popupTemplates.map((popup) => ({
          ...popup,
          elements: applyLogoToTemplate(
            popup.elements,
            newUserConfig.logo,
            newUserConfig.businessName
          ),
        }));

        return {
          ...updatedState,
          template: updatedTemplate,
          popupTemplates: updatedPopupTemplates,
          history: {
            past: [...state.history.past, { template: state.template, popupTemplates: state.popupTemplates }],
            future: [],
          },
        };
      }

      return updatedState;
    }

    case 'GENERATE_TEMPLATE': {
      cleanUpOldStorageEntires(); //Clear the storage due to no backend support (exceeding max storage limit)

      if (!state.userConfig) return state;

      // Preserve responsive logo variants for template generation.
      const logoForTemplate = state.userConfig?.logo;

      const generatedTemplate = getTemplateByIndustry(
        state.userConfig?.industry || DEFAULT_INDUSTRY,
        state.userConfig?.businessName || '',
        state.userConfig?.globalConfig || DEFAULT_GLOBAL_CONFIG,
        logoForTemplate
      );

      const extraPagesByTemplate =
        TEMPLATE_EXTRA_PAGES[state.userConfig?.industry || DEFAULT_INDUSTRY] ||
        [];

      const popupTemplates = getPopupTemplatesByIndustry(
        state.userConfig?.industry || DEFAULT_INDUSTRY,
        state.userConfig?.globalConfig || DEFAULT_GLOBAL_CONFIG
      );

      // Referece the templates for the extra pages
      extraPagesByTemplate.forEach((page) =>
        saveState(`${action.payload}-${page.title}`, {
          ...state,
          template: getTemplateByIndustry(
            page.industryName,
            state.userConfig?.businessName || '',
            state.userConfig?.globalConfig || DEFAULT_GLOBAL_CONFIG,
            logoForTemplate
          ),
          stage: 'editor',
          extraPages: extraPagesByTemplate,
          selectedPage: page.title,
          dynamicValues: page.dynamicValues,
          generalSettings: undefined,
          siteSettings: {
            ...state.siteSettings,
          },
        })
      );

      return {
        ...state,
        extraPages: extraPagesByTemplate,
        template: generatedTemplate,
        popupTemplates: popupTemplates,
      };
    }

    case 'REMOVE_ELEMENT': {
      const removeElementById = (
        elements: BuilderElement[]
      ): BuilderElement[] => {
        return elements.filter((element) => {
          if (element.id === action.payload) {
            return false;
          }

          if (element.children && element.children.length > 0) {
            element.children = removeElementById(element.children);
          }

          return true;
        });
      };

      // Create a deep clone of the current template state
      const currentTemplate = JSON.parse(JSON.stringify(state.template));

      const updatedElements = removeElementById(state.template.elements);

      const updatedTemplate = {
        ...state.template,
        elements: updatedElements,
      };

      // Also handle popup template if we're editing one
      let updatedPopupTemplates = state.popupTemplates;
      if (state.selectedPopupID && state.selectedPopupID !== 'none') {
        const selectedPopup = state.popupTemplates.find(
          (popup) => popup.id === state.selectedPopupID
        );

        if (selectedPopup) {
          const updatedPopupElements = removeElementById(
            selectedPopup.elements
          );
          const updatedPopupTemplate = {
            ...selectedPopup,
            elements: updatedPopupElements,
          };

          updatedPopupTemplates = state.popupTemplates.map((popup) =>
            popup.id === state.selectedPopupID ? updatedPopupTemplate : popup
          );
        }
      }

      return {
        ...state,
        template: updatedTemplate,
        popupTemplates: updatedPopupTemplates,
        selectedElementId: null,
        history: {
          past: [...state.history.past, currentTemplate],
          future: [],
        },
      };
    }

    case 'UNDO': {
      const { past, future } = state.history;
      if (past.length === 0) return state;

      const previous = past[past.length - 1];
      const newPast = past.slice(0, past.length - 1);

      if (!previous?.template) {
        console.warn('⚠️ Undo failed: Invalid history entry');
        return state;
      }

      return {
        ...state,
        template: previous.template,
        popupTemplates: previous.popupTemplates,
        history: {
          past: newPast,
          future: [{ template: state.template, popupTemplates: state.popupTemplates }, ...future],
        },
        selectedElementId: null,
      };
    }

    case 'REDO': {
      const { past, future } = state.history;
      if (future.length === 0) return state;

      const next = future[0];
      const newFuture = future.slice(1);

      if (!next?.template) {
        console.warn('⚠️ Redo failed: Invalid history entry');
        return state;
      }

      return {
        ...state,
        template: next.template,
        popupTemplates: next.popupTemplates,
        history: {
          past: [...past, { template: state.template, popupTemplates: state.popupTemplates }],
          future: newFuture,
        },
        selectedElementId: null,
      };
    }

    case 'RESET_HISTORY':
      return {
        ...state,
        history: {
          past: [],
          future: [],
        },
      };

    case 'SET_ZOOM':
      return {
        ...state,
        zoom: action.payload,
      };

    case 'RESET_ZOOM':
      return {
        ...state,
        zoom: 0.65,
      };

    case 'CHANGE_TEMPLATE_PAGE': {
      const templatePageId = action.payload;
      const pageTemplate = loadState(templatePageId);

      // Add null check for pageTemplate and userConfig
      if (!pageTemplate) {
        console.warn(
          `⚠️ Page template "${templatePageId}" not found in localStorage. This may happen after clearing browser cache.`
        );

        // Show a toast notification to the user
        if (typeof window !== 'undefined') {
          toast.error(
            'Unable to load page. The page data may have been cleared from browser storage.',
            { duration: 5000 }
          );
        }

        return state; // Return current state if page template doesn't exist
      }

      // Validate that the page template has essential properties
      if (!pageTemplate.template || !pageTemplate.template.elements) {
        console.warn(
          `⚠️ Page template "${templatePageId}" is missing essential data.`
        );

        if (typeof window !== 'undefined') {
          toast.error(
            'Page data is corrupted. Please try recreating the page.',
            { duration: 5000 }
          );
        }

        return state;
      }

      // Ensure the page the latest global settings when switching
      const mergedUserConfig = pageTemplate.userConfig
        ? {
            ...pageTemplate.userConfig,
            // Preserve global settings from current state
            businessName:
              state.userConfig?.businessName ||
              pageTemplate.userConfig?.businessName,
            logo: state.userConfig?.logo || pageTemplate.userConfig?.logo,
            globalConfig:
              state.userConfig?.globalConfig ||
              pageTemplate.userConfig?.globalConfig,
          }
        : state.userConfig;

      return {
        ...pageTemplate,
        popupTemplates: state.popupTemplates,
        userConfig: mergedUserConfig,
        // Ensure selectedPage is correct for URL/title updates
        selectedPage: pageTemplate.selectedPage || 'home',
      };
    }

    case 'SELECT_POPUP_PAGE': {
      const popupPageId = action.payload;
      return {
        ...state,
        selectedPopupID: popupPageId,
      };
    }

    case 'ADD_POPUP_TEMPLATE': {
      if (state.popupTemplates.find((popup) => popup.id === action.payload.id))
        return state;
      return {
        ...state,
        popupTemplates: [...state.popupTemplates, action.payload],
        selectedPopupID: action.payload.id,
      };
    }

    case 'UPDATE_POPUP_TEMPLATE': {
      const { id, template } = action.payload;
      return {
        ...state,
        popupTemplates: state.popupTemplates.map((popup) =>
          popup.id === id ? template : popup
        ),
      };
    }

    case 'DELETE_POPUP_TEMPLATE': {
      const popupId = action.payload;
      return {
        ...state,
        popupTemplates: state.popupTemplates.filter(
          (popup) => popup.id !== popupId
        ),
        selectedPopupID:
          state.selectedPopupID === popupId ? 'none' : state.selectedPopupID,
      };
    }

    case 'SET_SITE_SETTINGS': {
      return {
        ...state,
        siteSettings: action.payload,
      };
    }

    case 'SET_COMPONENTS': {
      return {
        ...state,
        components: action.payload,
      };
    }

    case 'SET_GENERAL_SETTINGS': {
      // Apply general settings to all page settings when they change
      if (action.landingPageID) {
        updateGeneralSettingsAcrossPages(
          action.landingPageID,
          action.payload,
          state.extraPages || []
        );
      }

      // Also update the current page's siteSettings immediately for UI refresh
      const updatedSiteSettings = {
        ...state.siteSettings,
        title: action.payload.title,
        language: action.payload.language,
        description: action.payload.description,
        showPageOnSearchEngine: action.payload.showPageOnSearchEngine,
        customCode: action.payload.customCode,
      };

      return {
        ...state,
        generalSettings: action.payload,
        siteSettings: updatedSiteSettings, // Update current page settings immediately
      };
    }

    case 'UPDATE_DYNAMIC_ELEMENT': {
      // Helper to update content by elementId
      const updateElementContent = (
        elements: BuilderElement[],
        dynamicContentId: string,
        content: string
      ): BuilderElement[] => {
        return elements.map((element) => {
          if (element.dynamicContentId === dynamicContentId) {
            return {
              ...element,
              content,
              ...(element.type === 'image' ? { src: content } : null),
            };
          }
          if (element.children) {
            return {
              ...element,
              children: updateElementContent(
                element.children,
                dynamicContentId,
                content
              ),
            };
          }
          return element;
        });
      };
      // Start with the current elements
      let updatedElements = state.template.elements;
      // Apply each update in the payload array
      for (const { elementId, content } of action.payload) {
        updatedElements = updateElementContent(
          updatedElements,
          elementId,
          content
        );
      }
      const updatedTemplate = {
        ...state.template,
        elements: updatedElements,
      };

      return {
        ...state,
        template: updatedTemplate,
        history: {
          past: [...state.history.past, { template: state.template, popupTemplates: state.popupTemplates }],
          future: [],
        },
      };
    }

    case 'APPLY_PAGE_DYNAMIC_VALUES': {
      // New action to apply dynamic values from page settings
      const { queryKey, queryValue } = action.payload;

      // Find matching dynamic value configuration in page settings first
      const pageConfig = state.siteSettings.dynamicValues?.find(
        (dv) => dv.queryKey === queryKey
      );

      let matchingValue;

      if (pageConfig) {
        matchingValue = pageConfig.values.find(
          (v) => v.queryParamValue === queryValue
        );
      }

      // If not found in page settings, check template values as fallback
      if (!matchingValue && state.dynamicValues) {
        const templateConfig = state.dynamicValues.find(
          (dv) => dv.queryKey === queryKey
        );
        if (templateConfig) {
          matchingValue = templateConfig.values.find(
            (v) => v.queryParamValue === queryValue
          );
        }
      }

      if (!matchingValue) return state;

      // Helper to update element content based on elementType
      const updateElementByType = (
        elements: BuilderElement[],
        elementId: string,
        content: string,
        elementType: 'content' | 'src' | 'href' = 'content'
      ): BuilderElement[] => {
        return elements.map((element) => {
          if (element.dynamicContentId === elementId) {
            const updates: Partial<BuilderElement> = {};

            switch (elementType) {
              case 'content':
                updates.content = content;
                break;
              case 'src':
                if (element.type === 'image') {
                  updates.src = content;
                }
                break;
              case 'href':
                if (element.type === 'link' || element.type === 'button') {
                  updates.href = content;
                }
                break;
            }

            return { ...element, ...updates };
          }
          if (element.children) {
            return {
              ...element,
              children: updateElementByType(
                element.children,
                elementId,
                content,
                elementType
              ),
            };
          }
          return element;
        });
      };

      // Start with the current elements
      let updatedElements = state.template.elements;

      // Apply each dynamic element update
      for (const dynamicElement of matchingValue.dynamicElements) {
        updatedElements = updateElementByType(
          updatedElements,
          dynamicElement.elementId,
          dynamicElement.content,
          dynamicElement.elementType || 'content' // Default to "content" for template values
        );
      }

      const updatedTemplate = {
        ...state.template,
        elements: updatedElements,
      };

      return {
        ...state,
        template: updatedTemplate,
        history: {
          past: [...state.history.past, { template: state.template, popupTemplates: state.popupTemplates }],
          future: [],
        },
      };
    }

    case 'SET_DYNAMIC_ELEMENT_VALUE': {
      return {
        ...state,
        dynamicElementValues: {
          ...state.dynamicElementValues,
          [action.payload.queryKey]: action.payload.value,
        },
      };
    }

    case 'SELECT_DYNAMIC_VALUE': {
      return {
        ...state,
        selectedDynamicValue: action.payload,
      };
    }

    case 'UPDATE_DYNAMIC_VALUE_ELEMENT': {
      if (!state.selectedDynamicValue) return state;

      const { dynamicContentId, elementType, content } = action.payload;
      const { queryKey, value: queryValue } = state.selectedDynamicValue;

      // Create a deep copy of site settings
      const updatedSiteSettings = {
        ...state.siteSettings,
        dynamicValues: state.siteSettings.dynamicValues
          ? [...state.siteSettings.dynamicValues]
          : [],
      };

      // Find or create the query configuration
      // First, check if it exists in page settings
      let queryConfig = updatedSiteSettings.dynamicValues.find(
        (dv) => dv.queryKey === queryKey
      );

      // If not found in page settings, check template dynamic values and copy the structure
      if (!queryConfig && state.dynamicValues) {
        const templateQueryConfig = state.dynamicValues.find(
          (dv) => dv.queryKey === queryKey
        );

        if (templateQueryConfig) {
          // Deep copy the template query structure to page settings
          queryConfig = {
            queryKey: templateQueryConfig.queryKey,
            values: templateQueryConfig.values.map((value) => ({
              queryParamValue: value.queryParamValue,
              dynamicElements: value.dynamicElements.map((element) => ({
                elementId: element.elementId,
                content: element.content,
                elementType: element.elementType,
                visible: element.visible,
              })),
            })),
          };
          updatedSiteSettings.dynamicValues.push(queryConfig);
        }
      }

      // If still not found, create a new query configuration
      if (!queryConfig) {
        queryConfig = {
          queryKey,
          values: [],
        };
        updatedSiteSettings.dynamicValues.push(queryConfig);
      } else {
        // Make a copy of the existing query config for immutability
        const queryIndex = updatedSiteSettings.dynamicValues.findIndex(
          (dv) => dv.queryKey === queryKey
        );
        queryConfig = { ...queryConfig, values: [...queryConfig.values] };
        updatedSiteSettings.dynamicValues[queryIndex] = queryConfig;
      }

      // Find or create the value configuration
      let valueConfig = queryConfig.values.find(
        (v) => v.queryParamValue === queryValue
      );

      if (!valueConfig) {
        valueConfig = {
          queryParamValue: queryValue,
          dynamicElements: [],
        };
        queryConfig.values.push(valueConfig);
      } else {
        // Make a copy of the value config for immutability
        const valueIndex = queryConfig.values.findIndex(
          (v) => v.queryParamValue === queryValue
        );
        valueConfig = {
          ...valueConfig,
          dynamicElements: [...valueConfig.dynamicElements],
        };
        queryConfig.values[valueIndex] = valueConfig;
      }

      // Find or create the element configuration
      // Search by both elementId AND elementType since an element can have multiple dynamic properties
      let elementConfig = valueConfig.dynamicElements.find(
        (el) =>
          el.elementId === dynamicContentId && el.elementType === elementType
      );

      if (!elementConfig) {
        // Create new element configuration and append it
        elementConfig = {
          elementId: dynamicContentId,
          content,
          elementType,
        };
        valueConfig.dynamicElements.push(elementConfig);

        // CRITICAL: When creating a NEW dynamic element, ensure ALL other values
        // in this query also get this element with their current template content
        // This prevents content bleeding across values
        if (state.dynamicValues) {
          const templateQuery = state.dynamicValues.find(
            (dv) => dv.queryKey === queryKey
          );
          if (templateQuery) {
            // For each other value in this query, add the element with ORIGINAL template content
            templateQuery.values.forEach((templateValue) => {
              if (templateValue.queryParamValue === queryValue) return; // Skip current value

              // Find this value in page settings
              let otherValueConfig = queryConfig.values.find(
                (v) => v.queryParamValue === templateValue.queryParamValue
              );

              if (!otherValueConfig) {
                // Create the value config if it doesn't exist
                otherValueConfig = {
                  queryParamValue: templateValue.queryParamValue,
                  dynamicElements: [],
                };
                queryConfig.values.push(otherValueConfig);
              }

              // Check if the element already exists for this value
              const elementExists = otherValueConfig.dynamicElements.some(
                (el) =>
                  el.elementId === dynamicContentId &&
                  el.elementType === elementType
              );

              if (!elementExists) {
                // Find the ORIGINAL template content for this specific value and element
                const originalTemplateElement =
                  templateValue.dynamicElements.find(
                    (el) =>
                      el.elementId === dynamicContentId &&
                      (el.elementType === elementType ||
                        (!el.elementType && elementType === 'content'))
                  );

                let contentForOtherValue = '';

                if (originalTemplateElement) {
                  // Use original template dynamic content if it exists
                  contentForOtherValue = originalTemplateElement.content;
                } else {
                  // Fallback: For NEW elements, extract actual element ID and use its content
                  // dynamicContentId format: __dynamic__actualElementId__
                  const actualElementId = dynamicContentId
                    .replace(/^__dynamic__/, '')
                    .replace(/__$/, '');
                  const templateElement = findElementById(
                    state.template.elements,
                    actualElementId
                  );
                  if (templateElement) {
                    contentForOtherValue =
                      elementType === 'src'
                        ? templateElement.src || ''
                        : elementType === 'href'
                        ? templateElement.href || ''
                        : templateElement.content || '';
                  }
                }

                otherValueConfig.dynamicElements.push({
                  elementId: dynamicContentId,
                  content: contentForOtherValue,
                  elementType,
                });
              }
            });
          }
        }
      } else {
        // Update existing element config for this specific property type
        const elementIndex = valueConfig.dynamicElements.findIndex(
          (el) =>
            el.elementId === dynamicContentId && el.elementType === elementType
        );
        valueConfig.dynamicElements[elementIndex] = {
          ...elementConfig,
          content,
          elementType,
        };
      }

      return {
        ...state,
        siteSettings: updatedSiteSettings,
      };
    }

    case 'DELETE_PROJECT': {
      const { projectId } = action.payload;

      // Storage key pattern: "{BUILDER_INTERFACE_LOCAL_STORAGE_KEYS}-{projectId}"
      const mainProjectKey = `${BUILDER_INTERFACE_LOCAL_STORAGE_KEYS}-${projectId}`;

      // Remove main project
      localStorage.removeItem(mainProjectKey);

      // Get all localStorage keys and find related project keys
      const allKeys = Object.keys(localStorage);
      const projectKeys = allKeys.filter(
        (key) =>
          key.startsWith(
            `${BUILDER_INTERFACE_LOCAL_STORAGE_KEYS}-${projectId}-`
          ) // Extra pages
      );

      // Remove all related project keys
      projectKeys.forEach((key) => {
        localStorage.removeItem(key);
      });

      // Reset state to initial state since project is deleted
      return {
        ...initialState,
        stage: 'registration',
      };
    }

    case 'RECOMPILE_TEMPLATE_WITH_GLOBAL_CONFIG': {
      if (!state.userConfig?.globalConfig || !state.userConfig?.industry) {
        return state;
      }

      console.log('🔄 Regenerating template with new global config');

      // Special handling for blank templates to preserve user-added elements
      if (state.userConfig?.industry === 'blankTemplate') {
        console.log(
          '🎯 Blank template detected - preserving existing elements and applying global config'
        );

        // Helper function to recursively update global config in elements
        const updateElementsWithGlobalConfig = (
          elements: BuilderElement[]
        ): BuilderElement[] => {
          return elements.map((element) => {
            const updatedElement = { ...element };

            // Update styles that might use global config values
            if (element.styles) {
              const updatedStyles = { ...element.styles };

              // Apply the compileElements function to update styles with new global config
              const tempElement = {
                ...updatedElement,
                styles: updatedStyles,
              };

              // Use the existing compileElements function to apply global config
              const compiledElement = compileElements(
                [tempElement],
                state.userConfig?.globalConfig || DEFAULT_GLOBAL_CONFIG
              )[0];
              if (compiledElement && compiledElement.styles) {
                updatedElement.styles = compiledElement.styles;
              }
            }

            // Recursively update children
            if (element.children && element.children.length > 0) {
              updatedElement.children = updateElementsWithGlobalConfig(
                element.children
              );
            }

            return updatedElement;
          });
        };

        const updatedTemplate = {
          ...state.template,
          elements: updateElementsWithGlobalConfig(state.template.elements),
        };

        console.log(
          '✅ Blank template global config updated successfully - elements preserved'
        );

        return {
          ...state,
          template: updatedTemplate,
          history: {
            past: [...state.history.past, { template: state.template, popupTemplates: state.popupTemplates }],
            future: [],
          },
        };
      }

      // For non-blank templates, regenerate the entire template as before
      const newTemplate = getTemplateByIndustry(
        state.userConfig?.industry || DEFAULT_INDUSTRY,
        state.userConfig?.businessName || '',
        state.userConfig?.globalConfig || DEFAULT_GLOBAL_CONFIG
      );

      const newPopupTemplates = getPopupTemplatesByIndustry(
        state.userConfig?.industry || DEFAULT_INDUSTRY,
        state.userConfig?.globalConfig || DEFAULT_GLOBAL_CONFIG
      );

      console.log('✅ Template regenerated successfully');

      return {
        ...state,
        template: newTemplate,
        popupTemplates: newPopupTemplates,
        history: {
          past: [...state.history.past, { template: state.template, popupTemplates: state.popupTemplates }],
          future: [],
        },
      };
    }

    case 'MOVE_ELEMENT': {
      const { elementId, targetContainerId, targetIndex, parentTabId } =
        action.payload;

      // Handle main template elements
      const updatedElements = moveElement(
        state.template.elements,
        elementId,
        targetContainerId,
        targetIndex,
        parentTabId
      );

      const updatedTemplate = {
        ...state.template,
        elements: updatedElements,
      };

      // Handle popup template elements if we're editing a popup
      let updatedPopupTemplates = state.popupTemplates;
      if (state.selectedPopupID && state.selectedPopupID !== 'none') {
        const selectedPopup = state.popupTemplates.find(
          (popup) => popup.id === state.selectedPopupID
        );

        if (selectedPopup) {
          const updatedPopupElements = moveElement(
            selectedPopup.elements,
            elementId,
            targetContainerId,
            targetIndex,
            parentTabId
          );

          const updatedPopupTemplate = {
            ...selectedPopup,
            elements: updatedPopupElements,
          };

          updatedPopupTemplates = state.popupTemplates.map((popup) =>
            popup.id === state.selectedPopupID ? updatedPopupTemplate : popup
          );
        }
      }

      return {
        ...state,
        template: updatedTemplate,
        popupTemplates: updatedPopupTemplates,
        history: {
          past: [...state.history.past, { template: state.template, popupTemplates: state.popupTemplates }],
          future: [],
        },
      };
    }

    case 'REORDER_ELEMENTS': {
      const { containerId, fromIndex, toIndex } = action.payload;

      // Handle main template elements
      const updatedElements = reorderElements(
        state.template.elements,
        containerId,
        fromIndex,
        toIndex
      );

      const updatedTemplate = {
        ...state.template,
        elements: updatedElements,
      };

      // Handle popup template elements if we're editing a popup
      let updatedPopupTemplates = state.popupTemplates;
      if (state.selectedPopupID && state.selectedPopupID !== 'none') {
        const selectedPopup = state.popupTemplates.find(
          (popup) => popup.id === state.selectedPopupID
        );

        if (selectedPopup) {
          const updatedPopupElements = reorderElements(
            selectedPopup.elements,
            containerId,
            fromIndex,
            toIndex
          );

          const updatedPopupTemplate = {
            ...selectedPopup,
            elements: updatedPopupElements,
          };

          updatedPopupTemplates = state.popupTemplates.map((popup) =>
            popup.id === state.selectedPopupID ? updatedPopupTemplate : popup
          );
        }
      }

      return {
        ...state,
        template: updatedTemplate,
        popupTemplates: updatedPopupTemplates,
        history: {
          past: [...state.history.past, { template: state.template, popupTemplates: state.popupTemplates }],
          future: [],
        },
      };
    }

    default:
      return state;
  }
};

// Create the context
const BuilderContext = createContext<BuilderContextType | undefined>(undefined);

// Helper function to sanitize loaded state
const sanitizeLoadedState = (
  loadedState: BuilderState | null | undefined
): BuilderState => {
  if (!loadedState) {
    console.log('📋 No saved state found - initializing with default state');
    return initialState;
  }

  try {
    // Validate that the loaded state has essential properties
    if (!loadedState.template || !loadedState.template.elements) {
      console.warn(
        '⚠️ Loaded state is missing essential properties - using initial state'
      );
      return initialState;
    }

    // Fix any duplicate IDs in the template elements
    const sanitizedTemplate = {
      ...loadedState.template,
      elements: validateAndFixIds(loadedState.template.elements),
    };

    // Fix any duplicate IDs in popup templates
    const sanitizedPopupTemplates =
      loadedState.popupTemplates?.map((popup) => ({
        ...popup,
        elements: validateAndFixIds(popup.elements),
      })) || [];

    const sanitizedState = {
      ...loadedState,
      template: sanitizedTemplate,
      popupTemplates: sanitizedPopupTemplates,
    };

    console.log('🧹 State sanitization completed');

    return sanitizedState;
  } catch (error) {
    console.error('❌ Failed to sanitize loaded state:', error);
    return initialState; // Return initial state if sanitization fails
  }
};

// Authentication function is imported from utils/fileUpload.ts

// Create the provider
export const BuilderProvider = ({
  children,
  initialSubPage,
  pageElementId,
}: {
  children: ReactNode;
  initialSubPage?: string;
  pageElementId?: string;
}) => {
  const { landingPageID, interfaceId } = useParams<{
    landingPageID: string;
    interfaceId?: string;
  }>();

  const resolveInitialBuilderState = () => {
    if (!landingPageID) {
      return initialState;
    }

    if (initialSubPage) {
      const subPageState = loadState(`${landingPageID}-${initialSubPage}`);
      if (subPageState) {
        return sanitizeLoadedState(subPageState);
      }
    }

    return sanitizeLoadedState(loadState(landingPageID));
  };

  const [state, rawDispatch] = useReducer(
    builderReducer,
    undefined,
    resolveInitialBuilderState
  );

  const previousStageRef = useRef<BuilderStage>(state.stage);
  const shouldSyncModuleRef = useRef(false);
  const pageElementIdRef = useRef(pageElementId);

  // Update pageElementIdRef when the prop changes or check localStorage
  useEffect(() => {
    let idToUse = pageElementId;

    // If no prop provided, check localStorage
    if (!idToUse && interfaceId) {
      const storedId = localStorage.getItem(`pageElement_${interfaceId}`);
      if (storedId) {
        idToUse = storedId;
        console.log('[BuilderContext] Found pageElementId in localStorage:', storedId);
      }
    }

    if (idToUse) {
      pageElementIdRef.current = idToUse;
      console.log('[BuilderContext] pageElementId updated:', idToUse);
    }
  }, [pageElementId, interfaceId]);

  // Create a custom dispatch function that includes landingPageID for relevant actions
  const dispatch = useCallback((action: BuilderAction) => {
    // Track SAVE_TEMPLATE action and capture current elements and interfaceId
    if (action.type === 'SAVE_TEMPLATE') {
      capturedTemplateElementsRef.current = state.template.elements;
      capturedInterfaceIdRef.current = interfaceId;
      setIsSaving(true);
      setSaveStatus('saving');
      setSaveTrigger(true);
    }

    // Handle SET_UNSAVED_CHANGES - update the state directly
    if (action.type === 'SET_UNSAVED_CHANGES') {
      setHasUnsavedChanges(action.payload);
      return;
    }

    if (action.type === 'SET_USER_CONFIG') {
      rawDispatch({
        ...action,
        landingPageID: landingPageID || '',
      });
    } else if (action.type === 'SET_GENERAL_SETTINGS') {
      rawDispatch({
        ...action,
        landingPageID: landingPageID || '',
      });
    } else {
      rawDispatch(action);
    }
  }, [state.template.elements, landingPageID]);

  const [selectedPreviewElement, setSelectedPreviewElement] =
    useState<BuilderElement | null>(null);

  const [publiishAccessToken, setPubliishAccessToken] = useState<string | null>(
    localStorage.getItem(AUTH_LOCAL_STORAGE_KEYS.PUBLISH_ACCESS_TOKEN)
  );

  // Module management state
  const [currentModuleId, setCurrentModuleId] = useState<string | null>(() => {
    // Check localStorage for module ID
    const storedModuleId = localStorage.getItem('module_id');
    if (storedModuleId) {
      // Verify the module still exists in cache
      const cachedModule = getModuleFromStorage(storedModuleId);
      if (cachedModule) {
        return storedModuleId;
      }
      // If cached module doesn't exist, clear it
      localStorage.removeItem('module_id');
    }
    return null;
  });
  const [isModuleLoading, setIsModuleLoading] = useState(false);
  const [hasCheckedCache, setHasCheckedCache] = useState(false);

  // Get project ID from landingPageID (explicit param) or let buildParams use env vars
  // Do NOT use localStorage for project_id in module queries
  const projectId = landingPageID || undefined;

  // Module hooks
  // Use filtering (fk[index][field]) for filtering by parent projectId
  // Standard query params (project_id, etc.) come from env vars for API routing
  // Note: fk[0][field] should be the database field name 'project_id' (not camelCase 'projectId')
  // Only enable when we have a projectId (landingPageID) and no currentModuleId
  const { data: modulesList } = useModules(
    projectId
      ? {
          filters: [{ field: 'website_project_id', value: projectId }],
        }
      : undefined,
    {
      enabled: !!projectId && !currentModuleId,
      meta: { suppressErrorToast: true },
    }
  );

  // Only fetch module data when we have a moduleId and landingPageID (projectId)
  const { data: moduleData } = useModule(
    currentModuleId || undefined,
    undefined, // Standard query params come from env vars via buildParams
    {
      enabled: !!currentModuleId && !!landingPageID,
      meta: { suppressErrorToast: true },
    }
  );

  const createModuleMutation = useCreateModule({
    meta: { suppressErrorToast: false },
  });

  const updateModuleMutation = useUpdateModule({
    meta: { suppressErrorToast: false },
  });

  // Element API hooks for save operation
  const createElementMutation = useCreateElement({
    meta: { suppressErrorToast: true },
  });
  const updateElementMutation = useUpdateElement({
    meta: { suppressErrorToast: true },
  });

  // Page element API hook for bulk save
  const updatePageElementsMutation = useUpdatePageElements({
    meta: { suppressErrorToast: true },
  });

  // Query client for cache invalidation
  const queryClient = useQueryClient();

  // Track when SAVE_TEMPLATE is dispatched
  const [saveTrigger, setSaveTrigger] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');

  // Capture template elements at the time SAVE_TEMPLATE is dispatched
  // This ensures we save the correct page's elements even if navigation happens
  const capturedTemplateElementsRef = useRef<BuilderElement[] | null>(null);

  // Capture interfaceId at the time SAVE_TEMPLATE is dispatched
  // This ensures we use the correct pageElementId for saving
  const capturedInterfaceIdRef = useRef<string | null>(null);

  // Track unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Store last saved template for comparison
  const lastSavedTemplateRef = useRef<Template | null>(null);

  // Track unsaved changes - simple approach:
  // - Set to true when user clicks Apply button (in PropertyEditor)
  // - Set to false when user clicks Save button
  // Also reset on initial load

  const hasLoggedStorageStateRef = useRef(false);
  useEffect(() => {
    if (hasLoggedStorageStateRef.current || !landingPageID) return;
    try {
      const storageKEY = `${BUILDER_INTERFACE_LOCAL_STORAGE_KEYS}-${landingPageID}`;
      const raw = localStorage.getItem(storageKEY);
      const parsedState = raw ? JSON.parse(raw) : null;
      console.log('🔍 Loaded builder state from localStorage:');
      console.log('🔍 Raw localStorage:', raw ? 'EXISTS' : 'NULL');
      console.log(
        '🔍 Global config colors:',
        parsedState?.userConfig?.globalConfig?.colors
      );
      console.log(
        '🔍 Current state colors:',
        state.userConfig?.globalConfig?.colors
      );
    } catch (err) {
      console.warn('Failed to read builder state from localStorage', err);
    } finally {
      hasLoggedStorageStateRef.current = true;
    }
  }, [landingPageID, state.userConfig?.globalConfig?.colors]);

  // Check for authentication token and authenticate if needed
  useEffect(() => {
    const checkAndAuthenticatePubliishAPI = async () => {
      const accessToken = localStorage.getItem(
        AUTH_LOCAL_STORAGE_KEYS.PUBLISH_ACCESS_TOKEN
      );

      if (!accessToken) {
        console.log('🔐 No access token found, authenticating...');
        const newToken = await authenticateUserPubliishAPI();
        if (newToken) {
          console.log('✅ Authentication successful, token saved');
          setPubliishAccessToken(newToken);
        } else {
          console.error('❌ Authentication failed');
        }
      } else {
        console.log('🔐 Access token found in localStorage');
        setPubliishAccessToken(accessToken);
      }
    };

    checkAndAuthenticatePubliishAPI();
  }, []);

  useEffect(() => {
    if (!state.template?.elements) return;
    const customFonts = collectCustomFonts(state.template.elements);
    if (customFonts.length > 0) {
      WebFont.load({
        google: { families: customFonts },
      });
    }
  }, [state.template.elements]);

  // Check cache first for module data
  useEffect(() => {
    if (hasCheckedCache) return;

    // If no projectId, mark cache as checked so module creation can proceed
    if (!projectId) {
      console.log('📦 No projectId available, skipping cache check');
      setHasCheckedCache(true);
      return;
    }

    // First, check if landingPageID is the module ID itself
    // Check if there's a stored module_id in localStorage
    const storedModuleId = localStorage.getItem(
      PROJECT_LOCAL_STORAGE_KEYS.MODULE_ID
    );

    // Determine module ID: prefer landingPageID (from URL) over storedModuleId (from localStorage)
    // This ensures switching between different websites on the dashboard works correctly
    const resolvedModuleId = landingPageID || storedModuleId;

    if (resolvedModuleId) {
      setCurrentModuleId(resolvedModuleId);
      localStorage.setItem(PROJECT_LOCAL_STORAGE_KEYS.MODULE_ID, resolvedModuleId);
      setHasCheckedCache(true);
      return; // Module ID found, skip further checks
    }

    // Check if we have a cached module for this project
    const cachedModule = getModuleByProjectId(projectId);
    if (cachedModule) {
      console.log('📦 Found cached module for project:', cachedModule.id);
      setCurrentModuleId(cachedModule.id);
      localStorage.setItem(
        PROJECT_LOCAL_STORAGE_KEYS.MODULE_ID,
        cachedModule.id
      );
      // Sync cached module data to builder state
      try {
        const { userConfig, generalSettings } =
          mapModuleToBuilderConfig(cachedModule);
        if (userConfig) {
          rawDispatch({
            type: 'SET_USER_CONFIG',
            payload: userConfig,
            landingPageID: landingPageID || '',
          });
        }
        if (generalSettings) {
          rawDispatch({
            type: 'SET_GENERAL_SETTINGS',
            payload: generalSettings,
            landingPageID: landingPageID || '',
          });
        }
      } catch (error) {
        console.error('❌ Failed to sync cached module data:', error);
      }
    }
    setHasCheckedCache(true);
  }, [projectId, landingPageID, hasCheckedCache]);

  // Find existing module for project when modules list loads
  useEffect(() => {
    if (currentModuleId || !modulesList?.data || !hasCheckedCache) return;

    // If projectId is available, find module by projectId
    // Otherwise, take the first module (if only one exists)
    let existingModule: WebsiteModule | undefined;

    if (projectId) {
      existingModule = modulesList.data.find(
        (module) => module.projectId === projectId
      );
    } else if (modulesList.data.length === 1) {
      // If no projectId but only one module exists, use it
      existingModule = modulesList.data[0];
    }

    if (existingModule) {
      console.log('📦 Found existing module for project:', existingModule.id);
      setCurrentModuleId(existingModule.id);
      // Cache the module data
      saveModuleToStorage(existingModule);
      if (landingPageID) {
        localStorage.setItem(
          PROJECT_LOCAL_STORAGE_KEYS.MODULE_ID,
          existingModule.id
        );
      }
    }
  }, [modulesList, projectId, currentModuleId, landingPageID, hasCheckedCache]);

  // Load module data and sync to builder state
  useEffect(() => {
    if (!moduleData?.data || !landingPageID) return;

    setIsModuleLoading(true);
    console.log('📥 Loading module data from API:', moduleData.data);
    try {
      // Cache the module data for offline access
      saveModuleToStorage(moduleData.data);

      const { userConfig, generalSettings } = mapModuleToBuilderConfig(
        moduleData.data,
        state.components,
      );

      // Always sync userConfig when landingPageID changes
      rawDispatch({
        type: 'SET_USER_CONFIG',
        payload: userConfig,
        landingPageID: landingPageID,
      });

      if (generalSettings) {
        rawDispatch({
          type: 'SET_GENERAL_SETTINGS',
          payload: generalSettings,
          landingPageID: landingPageID,
        });
      }
    } catch (error) {
      console.error('❌ Failed to sync module data:', error);
    } finally {
      setIsModuleLoading(false);
    }
  }, [moduleData, landingPageID, state.components]);

  // Create module if it doesn't exist and userConfig is available
  // NOTE: Module should already be created when "Continue" is clicked (on Index page)
  // This is ONLY a fallback for edge cases - should NEVER run when "Create Website" is clicked
  // because the module was already created on the Index page
  useEffect(() => {
    // FIRST: Check if there's a stored module_id in localStorage
    const storedModuleId = localStorage.getItem(
      PROJECT_LOCAL_STORAGE_KEYS.MODULE_ID
    );

    // Determine module ID: prefer landingPageID (from URL) over storedModuleId (from localStorage)
    // This ensures switching between different websites on the dashboard works correctly
    const resolvedModuleId = landingPageID || storedModuleId;

    if (resolvedModuleId) {
      // Only set if it's different to prevent infinite loops
      if (currentModuleId !== resolvedModuleId) {
        console.log(
          '📦 Using module ID:',
          resolvedModuleId,
          landingPageID ? '(from URL)' : '(from localStorage)'
        );
        setCurrentModuleId(resolvedModuleId);
        localStorage.setItem(PROJECT_LOCAL_STORAGE_KEYS.MODULE_ID, resolvedModuleId);
      }
      return; // Module already exists, NEVER create - update will handle it
    }

    // SECOND: Check other conditions - if any fail, don't create
    if (
      currentModuleId ||
      !state.userConfig ||
      isModuleLoading ||
      createModuleMutation.isPending ||
      !hasCheckedCache // Wait until cache check is complete
    )
      return;

    // THIRD: Check if module exists in the modules list (from API)
    if (modulesList?.data && modulesList.data.length > 0) {
      // Module already exists, don't create
      const existingModule = modulesList.data[0];
      // Only set if it's different to prevent infinite loops
      if (currentModuleId !== existingModule.id) {
        console.log(
          '📦 Module already exists in API, skipping creation:',
          existingModule.id
        );
        setCurrentModuleId(existingModule.id);
        localStorage.setItem(
          PROJECT_LOCAL_STORAGE_KEYS.MODULE_ID,
          existingModule.id
        );
      }
      return;
    }

    // Need projectId from landingPageID or module data to create module
    // If no projectId available, skip creation (will use env vars via buildParams)
    const moduleProjectId =
      projectId || state.userConfig?.businessName || undefined;
    if (!moduleProjectId) {
      console.warn('⚠️ Cannot create module: no projectId available');
      return;
    }

    // Check if we already tried to create (avoid infinite loops)
    // Note: This flag is cleared when going back to registration stage
    const createAttemptKey = `module_create_attempt_${moduleProjectId}`;
    if (localStorage.getItem(createAttemptKey)) {
      console.log(
        '⏭️ Skipping module creation: already attempted (flag exists)'
      );
      return;
    }

    console.log('📦 Creating new module for project:', moduleProjectId);
    localStorage.setItem(createAttemptKey, 'true');

    const modulePayload = mapBuilderConfigToModule(
      moduleProjectId,
      state.userConfig,
      state.generalSettings,
      state.components
    );

    createModuleMutation.mutate(
      {
        data: modulePayload as WebsiteModule,
        params: undefined, // Standard query params come from env vars via buildParams
        // Note: parent's projectId is in modulePayload.projectId (data field)
      },
      {
        onSuccess: (response) => {
          const newModuleId = response.data?.id;
          if (newModuleId) {
            console.log('✅ Module created successfully:', newModuleId);
            setCurrentModuleId(newModuleId);
            localStorage.setItem(
              PROJECT_LOCAL_STORAGE_KEYS.MODULE_ID,
              newModuleId
            );
            // Cache the newly created module (will be fetched on next load)
            // For now, we'll cache a minimal version
            const newModule: WebsiteModule = {
              id: newModuleId,
              projectId: moduleProjectId,
              name: state.userConfig?.businessName || '',
              industryType: state.userConfig?.industry || 'blankTemplate',
              ...modulePayload,
            } as WebsiteModule;
            saveModuleToStorage(newModule);
          }
        },
        onError: (error) => {
          console.error('❌ Failed to create module:', error);
          localStorage.removeItem(createAttemptKey);
        },
      }
    );
  }, [
    projectId,
    currentModuleId,
    state.userConfig,
    state.generalSettings,
    state.components,
    isModuleLoading,
    createModuleMutation,
    landingPageID,
    hasCheckedCache,
    modulesList?.data, // Check if module exists in API list
  ]);

  // Persist state to localStorage on changes
  useEffect(() => {
    const pageTemplateId =
      state.selectedPage === 'home'
        ? landingPageID
        : `${landingPageID}-${state.selectedPage}`;

    // Debounce saves to prevent excessive localStorage operations
    const timeoutId = setTimeout(() => {
      saveState(pageTemplateId, state);
    }, 500); // Wait 500ms before saving

    return () => clearTimeout(timeoutId);
  }, [landingPageID, state]);

  // Track if we've already initiated an update to prevent infinite loops
  const updateInProgressRef = useRef(false);
  const lastUpdatePayloadRef = useRef<string | null>(null);
  const moduleProjectIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (state.stage === 'preview' && previousStageRef.current !== 'preview') {
      shouldSyncModuleRef.current = true;
    }
    previousStageRef.current = state.stage;
  }, [state.stage]);

  // Trigger module sync when global config changes in editor stage
  useEffect(() => {
    if (state.stage === 'editor' && state.userConfig?.globalConfig) {
      shouldSyncModuleRef.current = true;
    }
  }, [state.stage, state.userConfig?.globalConfig]);

  // Store project_id from module data once when it's loaded (to avoid dependency on moduleData)
  useEffect(() => {
    if (moduleData?.data && !moduleProjectIdRef.current) {
      const module = moduleData.data as WebsiteModule & { website_project_id?: unknown };
      const projectId =
        extractOid(module.website_project_id) ||
        extractOid(module.projectId) ||
        localStorage.getItem(PROJECT_LOCAL_STORAGE_KEYS.PROJECT_ID);
      if (projectId) {
        moduleProjectIdRef.current = projectId;
        console.log('📌 Stored project_id:', projectId);
      }
    }

    // Fallback: get from cached module storage if not set
    if (!moduleProjectIdRef.current && currentModuleId) {
      const cachedModule = getModuleFromStorage(currentModuleId);
      if (cachedModule) {
        const module = cachedModule as WebsiteModule & { website_project_id?: unknown };
        const projectId =
          extractOid(module.website_project_id) ||
          extractOid(module.projectId) ||
          localStorage.getItem(PROJECT_LOCAL_STORAGE_KEYS.PROJECT_ID);
        if (projectId) {
          moduleProjectIdRef.current = projectId;
          console.log('📌 Stored project_id from cache:', projectId);
        }
      }
    }
  }, [moduleData?.data, currentModuleId]);

  // Debounced module update when userConfig or generalSettings change
  // This is triggered when "Create Website" is clicked (stage changes to "preview")
  // or when global config (typography/colors) changes in editor stage
  useEffect(() => {
    // Only update when stage is "preview" (after "Create Website" is clicked)
    // OR when in "editor" and shouldSyncModuleRef is true (global config changed)
    if (state.stage !== 'preview' && state.stage !== 'editor') {
      if (state.stage !== 'preview') {
        updateInProgressRef.current = false;
        lastUpdatePayloadRef.current = null;
      }
      return;
    }

    // For editor stage, only sync if shouldSyncModuleRef is true (global config change)
    if (state.stage === 'editor' && !shouldSyncModuleRef.current) {
      return;
    }

    if (
      !currentModuleId ||
      !state.userConfig ||
      !moduleProjectIdRef.current ||
      isModuleLoading ||
      updateModuleMutation.isPending ||
      updateInProgressRef.current
    )
      return;

    // Debounce module updates (2 seconds to avoid excessive API calls)
    const timeout = setTimeout(() => {
      const moduleProjectId = moduleProjectIdRef.current!;
      const modulePayload = mapBuilderConfigToModule(
        moduleProjectId,
        state.userConfig,
        state.generalSettings,
        state.components
      );

      // Create a hash of the payload to check if it's different from last update
      // Exclude description (timestamp) from comparison since it changes every time
      const payloadForComparison = { ...modulePayload };
      delete (payloadForComparison as Record<string, unknown>).description;
      const payloadHash = JSON.stringify(payloadForComparison);

      if (lastUpdatePayloadRef.current === payloadHash) {
        console.log(
          '⏭️ Skipping update: payload unchanged (excluding timestamp)'
        );
        return;
      }

      // Mark update as in progress
      updateInProgressRef.current = true;
      lastUpdatePayloadRef.current = payloadHash;

      updateModuleMutation.mutate(
        {
          id: currentModuleId,
          data: modulePayload as Partial<WebsiteModule>,
          params: undefined, // Standard query params come from env vars via buildParams
          // Note: parent's projectId is in modulePayload.projectId (data field)
        },
        {
          onSuccess: () => {
            console.log('✅ Module updated successfully');
            // Update cache with the new module data
            if (currentModuleId && modulePayload) {
              const updatedModule: WebsiteModule = {
                id: currentModuleId,
                projectId: moduleProjectId, // Use project_id from module data
                ...modulePayload,
              } as WebsiteModule;
              saveModuleToStorage(updatedModule);
            }
            shouldSyncModuleRef.current = false;
            // Reset update flag after a delay to allow for any state updates
            setTimeout(() => {
              updateInProgressRef.current = false;
            }, 1000);
          },
          onError: (error) => {
            console.error('❌ Failed to update module:', error);
            // Reset update flag on error so we can retry
            updateInProgressRef.current = false;
            lastUpdatePayloadRef.current = null;
          },
        }
      );
    }, 2000); // Wait 2 seconds before updating

    return () => {
      clearTimeout(timeout);
    };
  }, [
    state.stage,
    currentModuleId,
    state.userConfig,
    state.generalSettings,
    state.components,
    isModuleLoading,
    updateModuleMutation,
  ]);

  // Update URL and document title when page changes
  useEffect(() => {
    if (state.stage !== 'editor') return; // Only update URL in editor mode

    if (!landingPageID) return;

    const currentPath = window.location.pathname;

    // Skip URL modification if we're on the new backend-integrated editor route
    // The new route structure is /builder/{moduleId}/{interfaceId}/editor
    const isNewEditorRoute = currentPath.includes(`/${ROUTES.BUILDER_EDITOR}`);
    const isTemplatePreviewRoute = currentPath.includes(
      `/${ROUTES.BUILDER_TEMPLATE_PREVIEW}`
    );
    const isPublishRoute = currentPath.includes(
      `/${ROUTES.BUILDER_PUBLISH}`
    );

    if (!isNewEditorRoute && !isTemplatePreviewRoute && !isPublishRoute) {
      // Legacy flow: update URL based on selectedPage (for old localStorage-based system)
      const expectedPage = state.selectedPage || 'home';
      const expectedPath = `${ROUTES.BUILDER}/${landingPageID}/${expectedPage}`;

      // Update URL if it doesn't match the selected page
      if (currentPath !== expectedPath) {
        window.history.pushState({}, '', expectedPath);
      }
    }

    // Update document title
    const businessName =
      state.userConfig?.businessName || landingPageID || 'Landing Page';
    const pageTitle =
      state.selectedPage === 'home'
        ? businessName
        : `${state.selectedPage} - ${businessName}`;

    document.title = pageTitle;
  }, [
    state.selectedPage,
    state.stage,
    landingPageID,
    state.userConfig?.businessName,
  ]);

  const getSelectedElement = useCallback((): BuilderElement | null => {
    if (!state.selectedElementId) return null;
    const popupElements = state.popupTemplates.find(
      (popup) => popup.id === state.selectedPopupID
    )?.elements;
    return findElementById(
      popupElements || state.template.elements,
      state.selectedElementId
    );
  }, [state.selectedElementId, state.selectedPopupID, state.popupTemplates, state.template.elements]);

  // Mark element as synced (clear metadata flags) using reducer state
  const markElementAsSynced = useCallback((elementId: string, isCreate: boolean) => {
    rawDispatch({
      type: 'MARK_ELEMENT_AS_SYNCED',
      payload: { elementId },
    });
  }, []);

  // Reset selectedPreviewElement when element is deselected or deleted
  useEffect(() => {
    if (!state.selectedElementId) {
      setSelectedPreviewElement(null);
    }
  }, [state.selectedElementId]);

  // Handle save button - sync elements with metadata flags
  useEffect(() => {
    if (!saveTrigger) {
      return;
    }

    // Reset the trigger
    setSaveTrigger(false);

    // Use captured interfaceId if available (from page switch scenario), otherwise use current interfaceId
    // This ensures we save to the correct page_element
    const idToUseForSave = capturedInterfaceIdRef.current ?? interfaceId;
    capturedInterfaceIdRef.current = null;

    if (!idToUseForSave) {
      console.warn('⚠️ No interfaceId available for save');
      setIsSaving(false);
      setSaveStatus('error');
      return;
    }

    // Get the correct pageElementId from localStorage for the interface being saved
    const pageElementIdFromStorage = localStorage.getItem(`pageElement_${idToUseForSave}`);
    const elementIdToUse = pageElementIdFromStorage || idToUseForSave;

    // Use captured elements if available (from page switch scenario), otherwise use current state
    const elementsToSave = capturedTemplateElementsRef.current ?? state.template.elements;
    capturedTemplateElementsRef.current = null;

    // Use IIFE to allow async/await
    (async () => {
      // Clear sync flags from all elements - page_element handles persistence
      const clearSyncFlags = (elements: BuilderElement[]): BuilderElement[] => {
        return elements.map((el) => {
          const { _isNew, _needsSync, ...elementWithoutFlags } = el;
          return {
            ...elementWithoutFlags,
            children: el.children ? clearSyncFlags(el.children) : undefined,
          };
        });
      };

      const clearedElements = clearSyncFlags(elementsToSave);

      rawDispatch({
        type: 'SET_TEMPLATE',
        payload: {
          ...state.template,
          elements: clearedElements,
        },
      });

      // Update page_element with all elements (handles persistence)
      if (!elementIdToUse) {
        console.warn('⚠️ No page element ID available for save');
        toast.error('Save failed', {
          description: 'Unable to identify the target page for saving.',
        });
        setIsSaving(false);
        setSaveStatus('error');
        return;
      }

      try {
        await updatePageElementsMutation.mutateAsync({
          websiteInterfaceId: elementIdToUse,
          data: {
            website_interface_id: idToUseForSave || elementIdToUse,
            elements_data: clearedElements as never[],
          },
          params: undefined,
        });
        // Invalidate page elements cache to ensure fresh data on next load
        await queryClient.invalidateQueries({ queryKey: ['page-elements-list'], exact: false });
        await queryClient.invalidateQueries({ queryKey: ['page-elements'], exact: false });

        // Update last saved template and clear unsaved changes flag
        lastSavedTemplateRef.current = JSON.parse(JSON.stringify(state.template));
        setHasUnsavedChanges(false);
        rawDispatch({ type: 'RESET_HISTORY' });

        toast.success('Your changes have been saved!', {
          description: 'Your website design has been successfully saved.',
        });
        setSaveStatus('success');
      } catch (pageElementError) {
        console.error(`❌ Failed to update page_element:`, pageElementError);
        toast.error('Save failed', {
          description: 'We could not save your website design. Please try again.',
        });
        setSaveStatus('error');
      } finally {
        setIsSaving(false);
      }
    })();
  }, [saveTrigger, interfaceId, state.template, state.template.elements, queryClient, updatePageElementsMutation, rawDispatch]);

  return (
    <BuilderContext.Provider
      value={useMemo(
        () => ({
          state,
          dispatch,
          getSelectedElement,
          publiishAccessToken,
          selectedPreviewElement,
          setSelectedPreviewElement,
          markElementAsSynced,
          // Treat any undo history as unsaved work, in addition to explicit flags.
          hasUnsavedChanges:
            hasUnsavedChanges || state.history.past.length > 0,
          isSaving,
          saveStatus,
        }),
        [
          state,
          dispatch,
          getSelectedElement,
          publiishAccessToken,
          selectedPreviewElement,
          setSelectedPreviewElement,
          markElementAsSynced,
          hasUnsavedChanges,
          state.history.past.length,
          isSaving,
          saveStatus,
        ]
      )}
    >
      {children}
    </BuilderContext.Provider>
  );
};
// Create a hook to use the context
export const useBuilder = (): BuilderContextType => {
  const context = useContext(BuilderContext);
  if (!context) {
    throw new Error('useBuilder must be used within a BuilderProvider');
  }
  return context;
};
